import * as z from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import LabeledField from "providers/labeledField";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "providers/form";
import GenericButton from "widgets/controls/buttons/generic";

// Schema definition for the form
const AuditSchema = z.object({
  devices: z
    .array(
      z.object({
        snr: z.string(),
        actualReading: z.string(),
        lastReadingDate: z.string(),
      })
    )
    .optional(),
});

export type AuditFormType = z.infer<typeof AuditSchema>;

// Props for the form
type AuditFormProps = {
  submit: (data: AuditFormType) => void;
  clearData: () => void;
  onClose: () => void;
  defaultValues?: AuditFormType;
  values?: AuditFormType;
  loading?: boolean;
  disabled?: boolean;
};

const AuditForm = ({
  submit,
  clearData,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
}: AuditFormProps) => {
  const { t } = useTranslation();

  const form = useForm<AuditFormType>({
    resolver: zodResolver(AuditSchema),
    defaultValues,
    values,
  });

  const {
    control,
    watch,
    formState,
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const { errors } = formState;

  const onSubmit = async (formData: AuditFormType) => {
    try {
      await submit(formData);
      reset(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: "devices", // Handle array for steps
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className={`mb-2 grid grid-cols-3 gap-2`}>
          <LabeledField
            key={"header1"}
            className="text-txt-xs"
            label={"SNR"}
            id={"header1"}
            children={""}
            textColor="secondary"
          />
          <LabeledField
            key={"header2"}
            className="text-txt-xs"
            label={"ACTUAL READING"}
            id={"header2"}
            children={""}
            textColor="secondary"
          />
          <LabeledField
            key={"header3"}
            className="text-txt-xs"
            label={"LAST READING DATE"}
            id={"header3"}
            children={""}
            textColor="secondary"
          />
        </div>

        {fields.map((item, index) => (
          <div key={item.snr} className={`grid grid-cols-3 gap-2`}>
            {/* snr */}
            <FormField
              control={control}
              name={`devices.${index}.snr`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`snr-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* actualReading */}
            <FormField
              control={control}
              name={`devices.${index}.actualReading`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`actual-reading-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* last reading date */}
            <FormField
              control={control}
              name={`devices.${index}.lastReadingDate`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`last-reading-date-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}

        <div className="flex w-full flex-col pt-2">
          <div className="flex justify-end space-x-2">
            <GenericButton
              text={t("button.action.clear")}
              loading={loading || false}
              type="button"
              size="md"
              variant="outline"
              onClick={() => {
                clearData();
              }}
              color="secondary"
            />
            <GenericButton
              text={t("button.action.upload")}
              loading={loading || false}
              type="submit"
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default AuditForm;
